import { Injectable } from '@angular/core'
import { HttpParams } from '@angular/common/http'
import { Observable } from 'rxjs'
import { environment } from '../../environments/environment'
import { ApiService } from './api.service'

export interface StationAssignment {
  station_id: number
  station_name: string
  call_sign: string
  city: string | null
  state: string | null
  role: 'owner' | 'station_owner' | 'station_user'
  source: 'owner_group' | 'station_users'
  owner_name?: string
}

export interface StaffMember {
  user_id: number
  email: string
  firstname: string
  lastname: string
  global_role: string
  station_count: number
  station_assignments: StationAssignment[]
}

export interface StationSummary {
  station_id: number
  station_name: string
  call_sign: string
  city: string | null
  state: string | null
  staff_count: number
  staff: {
    user_id: number
    email: string
    firstname: string
    lastname: string
    role: string
  }[]
}

export interface StaffDirectoryPagination {
  total: number
  total_pages: number
  has_more: boolean
  limit?: number
  offset?: number
  current_page?: number
}

export interface StaffDirectoryResponse {
  status: string
  total_staff: number
  staff: StaffMember[]
  stations_summary: StationSummary[]
  /** By-person (staff) pager. */
  pagination: StaffDirectoryPagination
  /** By-station pager: stations_summary is sliced by stations_limit / stations_offset. */
  stations_total?: number
  stations_pagination?: StaffDirectoryPagination
}

@Injectable({ providedIn: 'root' })
export class StaffDirectoryService {
  private apiUrl = `${environment.apiUrl}/admin/staff-directory`

  constructor(private apiService: ApiService) {}

  getStaffDirectory(filters?: {
    email?: string
    station_id?: number
    role?: string
    search?: string
    limit?: number
    offset?: number
    /** Page over stations (by-station view); the backend defaults these to limit / offset. */
    stations_limit?: number
    stations_offset?: number
  }): Observable<StaffDirectoryResponse> {
    let params = new HttpParams()
    if (filters?.role) params = params.set('role', filters.role)
    if (filters?.search) params = params.set('search', filters.search)
    if (filters?.station_id != null) {
      params = params.set('station_id', filters.station_id.toString())
    }
    if (filters?.limit != null) params = params.set('limit', filters.limit.toString())
    if (filters?.offset !== undefined) {
      params = params.set('offset', filters.offset.toString())
    }
    if (filters?.stations_limit != null) {
      params = params.set('stations_limit', filters.stations_limit.toString())
    }
    if (filters?.stations_offset !== undefined) {
      params = params.set('stations_offset', filters.stations_offset.toString())
    }
    return this.apiService.authenticatedGet<StaffDirectoryResponse>(this.apiUrl, { params })
  }
}
